import * as React from "react";

import { Interpolation } from "@emotion/core";
import styles from "./height-transition.styles";

interface HeightTransitionProps {
  children: React.ReactNode;
  className?: string;
  transitionMargin?: number;
  transitionTime?: number;
  isClosed?: boolean;
}

const HeightTransition: React.FC<HeightTransitionProps> = ({
  isClosed,
  children,
  transitionMargin = 0,
  transitionTime = 0.5,
  className,
}) => {
  const contentRef: React.RefObject<HTMLDivElement> | null = React.useRef(null);

  const [height, setHeight] = React.useState<string | null>(
    isClosed ? "0" : null
  );
  const [isVisible, setVisible] = React.useState<boolean>(!isClosed);
  const clientHeight =
    (contentRef && contentRef.current && contentRef.current.clientHeight) ||
    null;

  React.useEffect(() => {
    if (
      !isClosed &&
      clientHeight &&
      height !== `${clientHeight + transitionMargin}`
    ) {
      setHeight(`${clientHeight + transitionMargin}`);
    }
    if (isClosed && height !== "0") {
      setHeight("0");
    }
  }, [isClosed, height, clientHeight, transitionMargin, className]);

  React.useEffect(() => {
    let visibleTimer: NodeJS.Timeout;
    if (!isClosed) {
      visibleTimer = setTimeout(() => {
        setVisible(!isClosed);
      }, transitionTime * 1000);
    } else {
      setVisible(!isClosed);
    }

    return () => {
      clearTimeout(visibleTimer);
    };
  }, [isClosed]);

  const maxHeightStyle: Interpolation = {
    maxHeight: height ? `${height}px` : "unset",
  };

  return (
    <div
      css={[styles.container, maxHeightStyle]}
      className={className}
      style={{
        transition: `max-height ${transitionTime}s ease-out`,
        overflow: isVisible ? "visible" : "hidden",
      }}
    >
      <div css={styles.content} ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export default HeightTransition;
